import React, { FC, useState } from 'react';
import styles from './Define.module.scss';
import { Form, Input, Checkbox, Select, Button } from 'antd';
import Actions from '../Actions/Actions';
import { RootState } from '../../../../rootReducer';
import { AppDispatch } from '../../../../store';
import {
  moveToNextPage,
  populatedDefineData,
  DefineAnnotationStandard,
  UserGroup,
  updateUserGroups,
  cloneAnnotationStandard
} from '../AnnotationStandardForm.slice';
import { connect } from 'react-redux';
import rootStyles from '../AnnotationStandardForm.module.scss';
import { restrictInputForVersionNumber } from '../../Annotation/AnnotationForm/Utils';
import { Link } from 'react-router-dom';

type Props = {
  storeName: string;
  storeStandardBody: string;
  storeVersion: string;
  storeDescription: string;
  storeReleased: boolean;
  storeDeprecated: boolean;
  storeParentAnnotationStandardId?: string;
  storeParentAnnotationStandardName?: string;
  storeParentAnnotationStandardVersion?: string;
  canEdit: boolean;
  userGroups: Array<UserGroup>;
  standardId: string | undefined;
  onMoveToNextPage: () => void;
  onPopulateDefineData: (data: DefineAnnotationStandard) => void;
  onUpdateSharedGroups: (id: string, shared: boolean) => void;
  onClone: () => void;
};

const { Option } = Select;

const Define: FC<Props> = ({
  storeName,
  storeStandardBody,
  storeVersion,
  storeReleased,
  storeDeprecated,
  storeDescription,
  storeParentAnnotationStandardId,
  storeParentAnnotationStandardName,
  storeParentAnnotationStandardVersion,
  canEdit,
  userGroups,
  standardId,
  onMoveToNextPage,
  onPopulateDefineData,
  onUpdateSharedGroups,
  onClone
}) => {
  const [name, setName] = useState<string>(storeName);
  const [standardBody, setStandardBody] = useState<string>(storeStandardBody);
  const [version, setVersion] = useState<string>(storeVersion ? storeVersion : '');
  const [released, setReleased] = useState<boolean>(storeReleased);
  const [deprecated, setDeprecated] = useState<boolean>(storeDeprecated);
  const [description, setDescription] = useState<string>(storeDescription);

  const onNextPage = () => {
    onPopulateDefineData({
      name,
      standardBody,
      version,
      released,
      deprecated,
      description
    });
    onMoveToNextPage();
  };

  const selectUserGroup = (value: string) => {
    onUpdateSharedGroups(value, true);
  };

  const deselectUserGroup = (value: string) => {
    onUpdateSharedGroups(value, false);
  };

  const populateVersion = (value: string) => {
    const newValue = restrictInputForVersionNumber(value) ? value : version;
    setVersion(newValue);
  };

  const isValid = () => name && standardBody && version;

  return (
    <div className={styles.defineContainer}>
      <div className={rootStyles.contentContainer}>
        {standardId && (
          <div className={styles.additionalActions}>
            {storeParentAnnotationStandardId && (
              <span className={styles.derivedFromSection}>
                Derived from&nbsp;
                <Link to={`/annotation-manager/annotation-standards/${storeParentAnnotationStandardId}`}>
                  {storeParentAnnotationStandardName} {storeParentAnnotationStandardVersion}
                </Link>
              </span>
            )}
            <Button onClick={onClone}>Clone</Button>
          </div>
        )}
        <Form layout='vertical'>
          <div>
            <Form.Item label='Standard name' className={styles.antFormItem} required>
              <Input disabled={!canEdit} placeholder='Enter Standard name' value={name} onChange={e => setName(e.target.value)} />
            </Form.Item>
            <Form.Item label='Standard body' className={styles.antFormItem} required>
              <Input disabled={!canEdit} placeholder='Enter Standard body' value={standardBody} onChange={e => setStandardBody(e.target.value)} />
            </Form.Item>
            <Form.Item label='Version' className={styles.antFormItem} required>
              <Input
                disabled={!canEdit}
                placeholder='Enter Version'
                defaultValue={version}
                value={version}
                onChange={e => populateVersion(e.target.value)}
              />
            </Form.Item>
            <Form.Item className={styles.antFormItem}>
              <Checkbox checked={released} onChange={e => setReleased(e.target.checked)} disabled={storeReleased}>
                Released
              </Checkbox>
            </Form.Item>
            <Form.Item className={styles.antFormItem}>
              <Checkbox checked={deprecated} onChange={e => setDeprecated(e.target.checked)}>
                Deprecated
              </Checkbox>
            </Form.Item>
            <Form.Item label='Description' className={styles.antFormItem}>
              <Input.TextArea
                disabled={!canEdit}
                placeholder='Enter Description'
                rows={10}
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </Form.Item>
            {userGroups && userGroups.length > 0 && (
              <Form.Item label='Shared with user groups' className={styles.antFormItem}>
                <Select
                  mode='multiple'
                  style={{ width: '100%' }}
                  placeholder='Please select'
                  onSelect={selectUserGroup}
                  onDeselect={deselectUserGroup}
                  defaultValue={userGroups.filter(x => x.isShared).map(({ userGroupId }) => userGroupId)}
                  showSearch={false}
                >
                  {userGroups.map((Item, index) => (
                    <Option value={Item.userGroupId} label={Item.name} selected={Item.isShared} key={index}>
                      <div>{Item.name}</div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </div>
        </Form>
      </div>
      <Actions isLoading={false} onNext={onNextPage} isDisabled={!isValid()} />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  storeName: state.annotationStandardForm.annotationStandard.name,
  storeStandardBody: state.annotationStandardForm.annotationStandard.standardBody,
  storeVersion: state.annotationStandardForm.annotationStandard.version,
  storeReleased: state.annotationStandardForm.annotationStandard.released,
  storeDeprecated: state.annotationStandardForm.annotationStandard.deprecated,
  storeDescription: state.annotationStandardForm.annotationStandard.description,
  canEdit: state.annotationStandardForm.canEdit,
  userGroups: state.annotationStandardForm.usergroups,
  standardId: state.annotationStandardForm.annotationStandard.id,
  storeParentAnnotationStandardId: state.annotationStandardForm.annotationStandard.parentAnnotationStandardId,
  storeParentAnnotationStandardName: state.annotationStandardForm.annotationStandard.parentAnnotationStandardName,
  storeParentAnnotationStandardVersion: state.annotationStandardForm.annotationStandard.parentAnnotationStandardVersion
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onMoveToNextPage: () => dispatch(moveToNextPage()),
  onPopulateDefineData: (data: DefineAnnotationStandard) => dispatch(populatedDefineData(data)),
  onUpdateSharedGroups: (id: string, shared: boolean) => dispatch(updateUserGroups(id, shared)),
  onClone: () => dispatch(cloneAnnotationStandard())
});

export default connect(mapStateToProps, mapDispatchToProps)(Define);
